import React from 'react';

export default function AllMetersView({
  plantName = 'Plant',
  meters = [],
  unit = 'm³',
  todayTotal = 0,
  onSelectMeter,
  onExport,
  onDownloadTable,
  onViewCert,
  onSetComplianceLimit,
  onSetInternalLimit,
  onOpenMeterPhoto,
}) {
  const activeCount = meters.length;

  return (
    <div id="allMeters">
      <div className="page-h">
        <div>
          <h1>{plantName} — all meters</h1>
          <p>{activeCount} meter{activeCount === 1 ? '' : 's'} assigned · plant-wide overview</p>
        </div>
        <div className="btnrow">
          <button className="btn" onClick={() => onOpenMeterPhoto && onOpenMeterPhoto()}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            Meter photo
          </button>
          <button
            className="btn"
            onClick={() => onSetInternalLimit && onSetInternalLimit()}
            title="Set internal operational warning threshold"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Set internal limit
          </button>
          <button
            className="btn primary"
            onClick={() => onSetComplianceLimit && onSetComplianceLimit()}
            title="Set statutory compliance limit"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 12l2 2 4-4M12 3l7 4v5c0 5-3.5 7.5-7 9-3.5-1.5-7-4-7-9V7z" />
            </svg>
            Set compliance limit
          </button>
        </div>
      </div>

      {/* 4 Stat Cards */}
      <div className="stat-strip" style={{ marginBottom: '16px' }}>
        <div className="stat" style={{ '--tint': 'rgba(14,164,114,.1)' }}>
          <div className="s-ic" style={{ background: 'var(--ok)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <div className="s-lab">Active meters</div>
          <div className="s-val">
            {activeCount} <u>/ {activeCount}</u>
          </div>
        </div>

        <div className="stat" style={{ '--tint': 'rgba(16,114,187,.08)' }}>
          <div className="s-ic">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18M7 15l4-5 3 3 5-7" />
            </svg>
          </div>
          <div className="s-lab">Today (plant total)</div>
          <div className="s-val">
            {Number(todayTotal).toFixed(2)} <u>{unit}</u>
          </div>
        </div>

        <div className="stat" style={{ '--tint': 'rgba(58,168,224,.14)' }}>
          <div className="s-ic" style={{ background: 'var(--surf)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12h18M12 3v18" />
            </svg>
          </div>
          <div className="s-lab">Total meters</div>
          <div className="s-val">
            {activeCount} <u>installed</u>
          </div>
        </div>

        <div className="stat" style={{ '--tint': 'rgba(229,72,77,.1)' }}>
          <div className="s-ic" style={{ background: 'var(--breach)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
            </svg>
          </div>
          <div className="s-lab">Compliance status</div>
          <div className="s-val">
            <span style={{ fontSize: '15px' }}>Active</span>
          </div>
        </div>
      </div>

      {/* Meter Details Table */}
      <div className="client-card" style={{ marginBottom: '16px' }}>
        <div className="card-h">
          <h3>Meter details</h3>
          <div className="ch-right">
            <span className="sub">select a meter row or dropdown above to drill down</span>
            <div className="btnrow">
              <button
                className="icon-dl xls"
                title="Download Excel"
                onClick={() => onDownloadTable && onDownloadTable('meters-details', 'xlsx')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6" />
                  <path d="M9.5 13l5 5M14.5 13l-5 5" />
                </svg>
              </button>
              <button
                className="icon-dl pdf"
                title="Download PDF"
                onClick={() => onDownloadTable && onDownloadTable('meters-details', 'pdf')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6" />
                  <path d="M9 14h6M9 17.5h4" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Serial no.</th>
                <th>Meter name</th>
                <th>Compliance limit ({unit})</th>
                <th>Internal limit ({unit})</th>
                <th>Status</th>
                <th>Communication</th>
                <th>Calibration cert</th>
              </tr>
            </thead>
            <tbody>
              {meters.length > 0 ? (
                meters.map((m, i) => (
                  <tr
                    key={i}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSelectMeter && onSelectMeter(m.value || m.productId)}
                  >
                    <td>
                      <b>{m.value || m.productId}</b>
                    </td>
                    <td>{m.name || m.productName || 'Borewell'}</td>
                    <td>
                      <span
                        style={{
                          fontWeight: 600,
                          color: (m.complianceLimit || 0) > 0 ? 'var(--primary-dk)' : 'var(--muted)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          background: (m.complianceLimit || 0) > 0 ? 'rgba(16,114,187,.08)' : 'transparent',
                        }}
                      >
                        {(m.complianceLimit || 0) > 0 ? `${Number(m.complianceLimit).toFixed(2)} ${unit}` : '0.00 (not set)'}
                      </span>
                    </td>
                    <td>
                      <span
                        style={{
                          fontWeight: 600,
                          color: (m.internalLimit || 0) > 0 ? 'var(--warn)' : 'var(--muted)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          background: (m.internalLimit || 0) > 0 ? 'rgba(230,126,34,.08)' : 'transparent',
                        }}
                      >
                        {(m.internalLimit || 0) > 0 ? `${Number(m.internalLimit).toFixed(2)} ${unit}` : '0.00 (not set)'}
                      </span>
                    </td>
                    <td>
                      <span className="cell-badge ok">Installed</span>
                    </td>
                    <td>
                      <span className="cell-badge live">Live</span>
                    </td>
                    <td>
                      {m.calibrationCertificate?.storedFileName || m.calibrationCertificate?.fileName ? (
                        <svg
                          className="pdf-ic"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          style={{ color: 'var(--primary-dk)', cursor: 'pointer' }}
                          title={`Download ${m.calibrationCertificate?.fileName || 'Calibration Certificate'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewCert &&
                              onViewCert(
                                m.value || m.productId,
                                m.calibrationCertificate?.fileName
                              );
                          }}
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <path d="M14 2v6h6" />
                        </svg>
                      ) : (
                        <span style={{ color: '#8aa1ad', fontSize: '13px' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', color: '#8aa1ad', padding: '24px' }}>
                    No meters found for this plant
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
